import { useMemo } from "react";
import { object, string, number } from "yup";
import type { BooleanSwitch } from "types";
import { BooleanSwitchValue } from "consts/general";
import { FormField } from "./types";

const useValidationSchema = () =>
    useMemo(() => (
        object().shape({
            [FormField.HasCreditLimitPolicy]: number()
                .nullable()
                .required()
                .oneOf([BooleanSwitchValue.On, BooleanSwitchValue.Off], 'Select Yes or No')
                .label('Has Credit Limit Policy'),

            [FormField.CreditLimitPolicyInput]: string()
                .nullable()
                .when(FormField.HasCreditLimitPolicy, {
                    is: (value: BooleanSwitch) => Object.is(value, BooleanSwitchValue.On),
                    then: validator => validator
                        .nullable()
                        .required('Credit Limit Policy is required')
                        .trim()
                        .min(10, 'Must be at least 10 characters')
                        .max(500, 'Cannot exceed 500 characters'),
                    otherwise: string()
                        .nullable()
                        .optional()
                })
                .label('Credit Limit Policy Input'),

            [FormField.FraudToolsInput]: string()
                .nullable()
                .required('Fraud Tools information is required')
                .trim()
                .min(20, 'Must be at least 20 characters')
                .max(600, 'Cannot exceed 600 characters')
                .label('Fraud Tools Input'),

            [FormField.WasTerminatedByAcquirer]: number()
                .nullable()
                .required()
                .oneOf([BooleanSwitchValue.On, BooleanSwitchValue.Off], 'Select Yes or No')
                .label('Was Terminated By Acquirer'),

            [FormField.TerminatedByAcquirerInput]: string()
                .nullable()
                .when(FormField.WasTerminatedByAcquirer, {
                    is: (value: BooleanSwitch) => Object.is(value, BooleanSwitchValue.On),
                    then: validator => validator
                        .nullable()
                        .required('Details on termination by acquirer are required')
                        .trim()
                        .min(10, 'Must be at least 10 characters')
                        .max(500, 'Cannot exceed 500 characters'),
                    otherwise: string()
                        .nullable()
                        .optional()
                })
                .label('Terminated By Acquirer Input'),

            [FormField.DoesCollectKyc]: number()
                .nullable()
                .required()
                .oneOf([BooleanSwitchValue.On, BooleanSwitchValue.Off], 'Select Yes or No')
                .label('Does Collect KYC'),

            [FormField.DoesCollectKycViaOther]: number()
                .nullable()
                .required()
                .oneOf([BooleanSwitchValue.On, BooleanSwitchValue.Off], 'Select Yes or No')
                .label('Does Collect KYC Via Other'),

            [FormField.CollectKycViaOtherInput]: string()
                .nullable()
                .when(FormField.DoesCollectKycViaOther, {
                    is: (value: BooleanSwitch) => Object.is(value, BooleanSwitchValue.On),
                    then: validator => validator
                        .nullable()
                        .required('Details on KYC collection methods are required')
                        .trim()
                        .min(2, 'Must be at least 2 characters')
                        .max(100, 'Cannot exceed 100 characters'),
                    otherwise: string()
                        .nullable()
                        .optional()
                })
                .label('Collect KYC Via Other Input'),

            [FormField.HasFullTimeCustomerSupport]: number()
                .nullable()
                .required()
                .oneOf([BooleanSwitchValue.On, BooleanSwitchValue.Off], 'Select Yes or No')
                .label('Has Full Time Customer Support'),

            [FormField.FullTimeCustomerSupportInput]: string()
                .nullable()
                .when(FormField.HasFullTimeCustomerSupport, {
                    is: (value: BooleanSwitch) => Object.is(value, BooleanSwitchValue.Off),
                    then: validator => validator
                        .nullable()
                        .required('Details on full-time customer support are required')
                        .trim()
                        .min(5, 'Must be at least 5 characters')
                        .max(100, 'Cannot exceed 100 characters'),
                    otherwise: string()
                        .nullable()
                        .optional()
                })
                .label('Full Time Customer Support Input'),

            [FormField.CustomerSupportEmail]: string()
                .nullable()
                .email('Enter a valid email address')
                .required('Customer Support Email is required')
                .label('Customer Support Email'),

            [FormField.RefundChargebackPolicies]: string()
                .nullable()
                .when(
                    [FormField.RefundChargebackPoliciesFileKey],
                    {
                        is: (fileKey: string | null) => !fileKey,
                        then: validator => validator
                            .nullable()
                            .min(20, 'Must be at least 20 characters')
                            .max(800, 'Must not exceed 800 characters')
                            .required('Either text or document is required'),
                        otherwise: validator => validator
                            .nullable()
                            .optional()
                    }
                ),

            [FormField.RefundChargebackPoliciesFileKey]: string()
                .nullable()
                .optional()
                .test(
                    'either-text-or-file',
                    'Either text or document is required',
                    function (fileKey) {
                        const { parent } = this;
                        const text = parent[FormField.RefundChargebackPolicies];

                        return Boolean(
                            (text && text.length >= 20) ||
                            fileKey
                        );
                    }
                ),

            [FormField.SecurityRiskManagementInput]: string()
                .nullable()
                .required('Security Risk Management information is required')
                .trim()
                .min(20, 'Must be at least 20 characters')
                .max(800, 'Cannot exceed 800 characters')
                .label('Security Risk Management Input'),

            [FormField.OtherSecurityRiskInput]: string()
                .nullable()
                .optional()
                .trim()
                .min(10, 'Must be at least 10 characters')
                .max(800, 'Cannot exceed 800 characters')
                .label('Other Security Risk Input')
        })
    ), []);

export default useValidationSchema;
